/**
 * Hooks TanStack Query: merch AiPeces (store_products) + colaboradores.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { useAuth } from '@/hooks/useAuth';
import type {
    LandingPartnerRow,
    LandingPartnerUpsertInput,
    StoreMerchProduct,
} from '../types';

const QK = {
    merch: ['admin', 'store-propios', 'merch'] as const,
    partners: (status: string, search: string) =>
        ['admin', 'store-propios', 'partners', status, search] as const,
};

function asMerchProducts(data: unknown): StoreMerchProduct[] {
    if (!data) return [];
    if (Array.isArray(data)) return data as StoreMerchProduct[];
    // Algunas capas envuelven el JSONB en un elemento
    const norm = normalizeRpcResponse(data);
    if (norm.length === 1 && Array.isArray(norm[0])) {
        return norm[0] as StoreMerchProduct[];
    }
    return norm as StoreMerchProduct[];
}

export function useStoreMerchCatalog() {
    const { user } = useAuth();
    return useQuery<StoreMerchProduct[]>({
        queryKey: QK.merch,
        enabled: Boolean(user?.id),
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_store_catalog', {
                p_admin_user_id: user!.id,
            });
            if (error) throw new Error(error.message);
            return asMerchProducts(data);
        },
        staleTime: 20_000,
    });
}

export function useUpdateStoreMerchProduct() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            productId: string;
            name?: string;
            description?: string | null;
            category?: string;
            isActive?: boolean;
            sortOrder?: number;
            images?: string[];
        }) => {
            if (!user?.id) throw new Error('Sin sesión admin');
            const params: Record<string, unknown> = {
                p_admin_user_id: user.id,
                p_product_id: input.productId,
            };
            if (input.name !== undefined) params.p_name = input.name;
            if (input.description !== undefined) params.p_description = input.description;
            if (input.category !== undefined) params.p_category = input.category;
            if (input.isActive !== undefined) params.p_is_active = input.isActive;
            if (input.sortOrder !== undefined) params.p_sort_order = input.sortOrder;
            if (input.images !== undefined) params.p_images = input.images;
            const { data, error } = await dbClient.rpc('admin_update_store_product', params);
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: QK.merch });
        },
    });
}

export function useUpdateStoreMerchVariant() {
    const { user } = useAuth();
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            variantId: string;
            priceCents?: number;
            stock?: number | null;
            clearStock?: boolean;
            isActive?: boolean;
            stripePriceId?: string | null;
        }) => {
            if (!user?.id) throw new Error('Sin sesión admin');
            const params: Record<string, unknown> = {
                p_admin_user_id: user.id,
                p_variant_id: input.variantId,
            };
            if (input.priceCents !== undefined) params.p_price_cents = input.priceCents;
            if (input.clearStock) {
                params.p_clear_stock = true;
            } else if (input.stock !== undefined && input.stock !== null) {
                params.p_stock = input.stock;
            }
            if (input.isActive !== undefined) params.p_is_active = input.isActive;
            if (input.stripePriceId !== undefined) {
                params.p_stripe_price_id = input.stripePriceId ?? '';
            }
            const { data, error } = await dbClient.rpc('admin_update_store_variant', params);
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: QK.merch });
        },
    });
}

export function useLandingPartnersList(status: string, search: string) {
    return useQuery<LandingPartnerRow[]>({
        queryKey: QK.partners(status, search),
        queryFn: async () => {
            const params: Record<string, unknown> = {
                p_limit: 200,
                p_offset: 0,
            };
            if (status && status !== 'all') params.p_status = status;
            if (search.trim()) params.p_search = search.trim();
            const { data, error } = await dbClient.rpc('admin_landing_partners_list', params);
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as LandingPartnerRow[];
        },
        staleTime: 20_000,
    });
}

export function useUpsertLandingPartner() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: LandingPartnerUpsertInput) => {
            const params: Record<string, unknown> = {};
            if (input.id) params.p_id = input.id;
            if (input.slug !== undefined) params.p_slug = input.slug;
            if (input.name !== undefined) params.p_name = input.name;
            if (input.partnerType !== undefined) params.p_partner_type = input.partnerType;
            if (input.description !== undefined) params.p_description = input.description;
            if (input.logoUrl !== undefined) params.p_logo_url = input.logoUrl;
            if (input.websiteUrl !== undefined) params.p_website_url = input.websiteUrl;
            if (input.countryCode !== undefined) params.p_country_code = input.countryCode;
            if (input.sortOrder !== undefined) params.p_sort_order = input.sortOrder;
            if (input.status !== undefined) params.p_status = input.status;
            if (input.tagline !== undefined) params.p_tagline = input.tagline;
            if (input.coverImageUrl !== undefined) params.p_cover_image_url = input.coverImageUrl;
            if (input.highlights !== undefined) params.p_highlights = input.highlights;
            if (input.sponsorClubSlugs !== undefined) {
                params.p_sponsor_club_slugs = input.sponsorClubSlugs;
            }
            if (input.sponsorEventSlugs !== undefined) {
                params.p_sponsor_event_slugs = input.sponsorEventSlugs;
            }
            if (input.articleUrl !== undefined) params.p_article_url = input.articleUrl;
            if (input.isFeatured !== undefined) params.p_is_featured = input.isFeatured;

            const { data, error } = await dbClient.rpc('admin_landing_partners_upsert', params);
            if (error) throw new Error(error.message);
            const norm = normalizeRpcResponse(data);
            return (norm[0] as string | undefined) ?? (data as string);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin', 'store-propios', 'partners'] });
        },
    });
}

export function useSetLandingPartnerStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { id: string; status: string }) => {
            const { error } = await dbClient.rpc('admin_landing_partners_set_status', {
                p_id: input.id,
                p_status: input.status,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ['admin', 'store-propios', 'partners'] });
        },
    });
}
